import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Puff } from 'react-loader-spinner'
import { IoIosAdd } from 'react-icons/io'
import { BiTrash } from 'react-icons/bi'
import Button from '../../components/Button'
import Table from '../../components/Table'
import Confirmation from '../../components/Confirmation/Confirmation'
import HealthgroupModal from './Modal'
import styles from './styles.module.scss'
import { getHealthgroup, deleteHealthgroup } from '../../actions/medCenter/healthGroups'

const TABLE_HEADERS = [
  { key: 'title', label: 'Название' },
  { key: 'actions', label: '', width: '60px' }
]

export default function Healthgroup() {
  const dispatch = useDispatch()
  const { healthgroup, loading } = useSelector(state => state.directoryMedCenter)

  const [selectedItem, setSelectedItem] = useState(null)
  const [confirmation, setConfirmation] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [sortAsc, setSortAsc] = useState(true)

  useEffect(() => {
    dispatch(getHealthgroup())
  }, [dispatch])

  const openModal = useCallback((item) => () => {
    setSelectedItem(item || {})
  }, [])

  const closeModal = useCallback(() => {
    setSelectedItem(null)
  }, [])

  const handleDelete = useCallback((item) => (e) => {
    e.stopPropagation()
    setConfirmation({ id: item.hid, name: item.title })
  }, [])

  const acceptConfirmation = useCallback(async () => {
    if (!confirmation) return
    setIsDeleting(true)
    try {
      await dispatch(deleteHealthgroup(confirmation.id))
    } finally {
      setConfirmation(null)
      setIsDeleting(false)
    }
  }, [dispatch, confirmation])

  const rejectConfirmation = useCallback(() => {
    setConfirmation(null)
  }, [])

  const sortedList = useMemo(() => {
    if (!healthgroup) return []
    return [...healthgroup].sort((a, b) => sortAsc
      ? a.title?.localeCompare(b.title, 'ru')
      : b.title?.localeCompare(a.title, 'ru'))
  }, [healthgroup, sortAsc])

  const isModalOpen = selectedItem !== null
  const isAddMode = selectedItem && !selectedItem.hid

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <IoIosAdd size={64} className={styles.emptyIcon} />
      <p>Нет добавленных групп здоровья</p>
      <Button onClick={openModal()}>Добавить первую группу здоровья</Button>
    </div>
  )

  const renderLoading = () => (
    <div className={styles.loadingState}>
      <Puff
        height={50}
        width={50}
        color="#3689FF"
        ariaLabel="Загрузка..."
      />
      <span>Загрузка групп здоровья...</span>
    </div>
  )

  const renderTable = () => (
    <div
      className={styles.tableContainer}
      style={{ '--table-width': `${TABLE_HEADERS.length * 15}vw` }}
    >
      <Table className={styles.table}>
        <thead>
          <tr>
            {TABLE_HEADERS.map(({ key, label, width }) => (
              <th
                key={key}
                style={{ ...(width ? { width } : {}), ...(key === 'title' ? { cursor: 'pointer' } : {}) }}
                onClick={key === 'title' ? () => setSortAsc(v => !v) : undefined}
              >{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedList.map((item) => (
            <tr
              key={item.hid}
              onClick={openModal(item)}
              className={styles.tableRow}
            >
              <td>
                <span className={styles.itemName}>{item.title}</span>
              </td>
              <td>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={handleDelete(item)}
                  title={`Удалить группу здоровья ${item.title}`}
                  aria-label={`Удалить группу здоровья ${item.title}`}
                >
                  <BiTrash size={20} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className={styles.actions}>
        <Button onClick={openModal()}>
          <IoIosAdd size={20} style={{ marginRight: 8 }} />
          Добавить
        </Button>
        <span className={styles.counter}>
          Всего: {sortedList.length}
        </span>
      </div>
    </div>
  )

  return (
    <div className={styles.wrapper}>
      <header className={styles.header}>
        <h1>Группы здоровья</h1>
      </header>

      <main className={styles.content}>
        {loading && renderLoading()}
        {!loading && sortedList.length === 0 && renderEmptyState()}
        {!loading && sortedList.length > 0 && renderTable()}
      </main>

      <HealthgroupModal
        open={isModalOpen}
        onClose={closeModal}
        data={selectedItem}
        addMode={isAddMode}
      />

      {confirmation && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Вы уверены, что хотите удалить ${confirmation.name}?`}
          acceptConfirmation={acceptConfirmation}
          rejectConfirmation={rejectConfirmation}
          loading={isDeleting}
        />
      )}
    </div>
  )
}
